/**
 * RoleOrientationCards — Shown on onboarding completion to orient admins
 * to role-specific capabilities and immediate high-value actions.
 */
import { motion } from "framer-motion";
import {
  Crown, Users, CalendarDays, Megaphone, ArrowRight,
  BarChart3, ShieldAlert, Mail, Globe, Trophy,
  UserPlus, Brain, Layers, Kanban,
  type LucideIcon,
} from "lucide-react";
import { useWorkspace, type WorkspaceRole } from "@/hooks/useWorkspace";

interface OrientationCard {
  icon: LucideIcon;
  title: string;
  whyItMatters: string;
  quickAction: string;
  route: string;
}

const ROLE_ORIENTATION: Record<WorkspaceRole, {
  greeting: string;
  subtitle: string;
  cards: OrientationCard[];
}> = {
  executive_director: {
    greeting: "Your command center is ready",
    subtitle: "MemberOS gives you instant visibility into org health, member engagement, and revenue — all in one place.",
    cards: [
      {
        icon: BarChart3,
        title: "Real-time org health dashboard",
        whyItMatters: "See membership trends, revenue, and engagement signals at a glance — no spreadsheets needed.",
        quickAction: "View Dashboard",
        route: "/",
      },
      {
        icon: ShieldAlert,
        title: "Retention intelligence",
        whyItMatters: "AI-powered risk scoring identifies members likely to lapse before renewal, so you can act early.",
        quickAction: "Check Retention",
        route: "/retention",
      },
      {
        icon: Brain,
        title: "Board-ready reports",
        whyItMatters: "Generate professional reports showing value delivered, engagement metrics, and strategic recommendations.",
        quickAction: "Generate Report",
        route: "/reports",
      },
    ],
  },
  membership_pro: {
    greeting: "Your member management hub is ready",
    subtitle: "Everything you need to recruit, onboard, engage, and retain members — powered by intelligent automation.",
    cards: [
      {
        icon: UserPlus,
        title: "Smart member pipeline",
        whyItMatters: "Track prospects from first contact through conversion with built-in CRM and lifecycle tracking.",
        quickAction: "View CRM",
        route: "/crm",
      },
      {
        icon: Layers,
        title: "Membership tiers & benefits",
        whyItMatters: "Configure tiered membership with automated benefit delivery and entitlement tracking.",
        quickAction: "Set Up Tiers",
        route: "/member-success?tab=plans",
      },
      {
        icon: ShieldAlert,
        title: "Renewal risk monitoring",
        whyItMatters: "Know exactly who needs attention and when, with AI-scored renewal risk predictions.",
        quickAction: "Review Risks",
        route: "/retention",
      },
    ],
  },
  event_planner: {
    greeting: "Your event management suite is ready",
    subtitle: "Plan, promote, and execute events that drive engagement — from intimate workshops to signature galas.",
    cards: [
      {
        icon: CalendarDays,
        title: "Full event lifecycle",
        whyItMatters: "Create events with ticketing, registration, check-in, and automated follow-up — all connected.",
        quickAction: "Create Event",
        route: "/events",
      },
      {
        icon: Kanban,
        title: "Programming calendar",
        whyItMatters: "See your entire event pipeline at a glance. Spot gaps and plan a balanced calendar.",
        quickAction: "View Calendar",
        route: "/programming-calendar",
      },
      {
        icon: Trophy,
        title: "Signature event planning",
        whyItMatters: "Major events deserve dedicated tracking — milestones, sponsors, budgets, and timelines.",
        quickAction: "Plan Signature Event",
        route: "/signature-events",
      },
    ],
  },
  marketing_comms: {
    greeting: "Your communications toolkit is ready",
    subtitle: "Reach the right members with the right message — newsletters, campaigns, website, and funnels.",
    cards: [
      {
        icon: Mail,
        title: "Email campaigns & newsletters",
        whyItMatters: "Keep members informed and engaged with professional communications — templates included.",
        quickAction: "Start a Campaign",
        route: "/communications",
      },
      {
        icon: Globe,
        title: "Website builder",
        whyItMatters: "Your public-facing website updates automatically with events, member content, and resources.",
        quickAction: "Edit Website",
        route: "/website",
      },
      {
        icon: Megaphone,
        title: "Lead funnels",
        whyItMatters: "Capture prospects with landing pages and convert them into members with automated follow-up.",
        quickAction: "Build a Funnel",
        route: "/funnels",
      },
    ],
  },
};

interface RoleOrientationCardsProps {
  onNavigate: (path: string) => void;
}

export default function RoleOrientationCards({ onNavigate }: RoleOrientationCardsProps) {
  const { activeWorkspace } = useWorkspace();

  if (!activeWorkspace) return null;

  const config = ROLE_ORIENTATION[activeWorkspace];
  if (!config) return null;

  return (
    <div className="space-y-4">
      <div className="text-center space-y-1">
        <h3 className="text-base font-semibold text-foreground">{config.greeting}</h3>
        <p className="text-xs text-muted-foreground max-w-md mx-auto">{config.subtitle}</p>
      </div>

      <div className="space-y-2">
        {config.cards.map((card, i) => (
          <motion.button
            key={card.title}
            type="button"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            onClick={() => onNavigate(card.route)}
            className="w-full flex items-start gap-3 p-3 rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-sm transition-all text-left group"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <card.icon className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{card.title}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{card.whyItMatters}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity mt-2">
              {card.quickAction}
              <ArrowRight className="w-3 h-3" />
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
